/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useStyletron } from "baseui";
import { StatefulPopover, PLACEMENT } from "baseui/popover";
import { BackgroundVariant } from "@xyflow/react";
import {
  FaSun,
  FaMoon,
  FaLowVision,
  FaAdjust,
  FaChevronDown,
  FaDownload,
} from "react-icons/fa";
import { MdBugReport, MdPassword } from "react-icons/md";
import { DeleteAlt } from "baseui/icon";
import { useApolloClient } from "@apollo/client";
import { DexterAuthContext } from "../Utils/AuthContext";
import {
  LOGO_URL,
  LOGO_URL_COLORBLIND_DARK,
  LOGO_URL_COLORBLIND_LIGHT,
} from "../Utils/SiteLogo.mjs";
import {
  useDexterTheme,
  CanvasBackground,
  NO_CANVAS_BACKGROUND,
} from "../DesignSystem/Theme/ThemeContext";
import { themes, ThemeNames } from "../DesignSystem/Theme/Theme";
import { ProjectInfo } from "../UserApp";
import { StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../DesignSystem/TooltipStyles";
import { Routes } from "../Utils/Routes";
import { gql, useQuery } from "@apollo/client";
import appInfo from "../Api/Gql/AppInfo";
import { AppInfo } from "Api";
import { generateDocumentationPdf } from "../Reference/Documentation/Pdf/DocumentationPdf";
import { notify } from "../Shared/Notify";

const THEME_LOGOS: Record<string, URL> = {
  [ThemeNames.LIGHT]: LOGO_URL_COLORBLIND_LIGHT,
  [ThemeNames.DARK]: LOGO_URL_COLORBLIND_DARK,
  [ThemeNames.COLORBLIND_LIGHT]: LOGO_URL_COLORBLIND_LIGHT,
  [ThemeNames.COLORBLIND_DARK]: LOGO_URL_COLORBLIND_DARK,
};

const THEME_LABELS: Record<string, string> = {
  [ThemeNames.LIGHT]: "Light",
  [ThemeNames.DARK]: "Dark",
  [ThemeNames.COLORBLIND_LIGHT]: "Colorblind Light",
  [ThemeNames.COLORBLIND_DARK]: "Colorblind Dark",
};

const BG_LABELS: Record<CanvasBackground, string> = {
  [BackgroundVariant.Dots]: "Dots",
  [BackgroundVariant.Lines]: "Lines",
  [BackgroundVariant.Cross]: "Cross",
  [NO_CANVAS_BACKGROUND]: "None",
};

const BG_VARIANTS: CanvasBackground[] = [
  BackgroundVariant.Dots,
  BackgroundVariant.Lines,
  BackgroundVariant.Cross,
  NO_CANVAS_BACKGROUND,
];

// Drawn canvas background options
function BgVariantIcon({
  variant,
  color,
}: {
  variant: CanvasBackground;
  color: string;
}): JSX.Element {
  const size = 20;
  if (variant === NO_CANVAS_BACKGROUND) {
    return (
      <svg width={size} height={size} viewBox="0 0 20 20">
        <rect
          x={2}
          y={2}
          width={16}
          height={16}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
        />
        <line x1={4} y1={16} x2={16} y2={4} stroke={color} strokeWidth={1.5} />
      </svg>
    );
  }
  if (variant === BackgroundVariant.Lines) {
    return (
      <svg width={size} height={size} viewBox="0 0 20 20">
        {[3, 8, 13, 18].map((y) => (
          <line
            key={y}
            x1={0}
            y1={y}
            x2={20}
            y2={y}
            stroke={color}
            strokeWidth={1.5}
          />
        ))}
      </svg>
    );
  }
  if (variant === BackgroundVariant.Dots) {
    return (
      <svg width={size} height={size} viewBox="0 0 20 20">
        {[4, 10, 16].flatMap((x) =>
          [4, 10, 16].map((y) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={1.4} fill={color} />
          ))
        )}
      </svg>
    );
  }
  // Cross
  return (
    <svg width={size} height={size} viewBox="0 0 20 20">
      {[4, 10, 16].flatMap((x) =>
        [4, 10, 16].map((y) => (
          <g key={`${x}-${y}`}>
            <line
              x1={x - 2}
              y1={y}
              x2={x + 2}
              y2={y}
              stroke={color}
              strokeWidth={1.5}
            />
            <line
              x1={x}
              y1={y - 2}
              x2={x}
              y2={y + 2}
              stroke={color}
              strokeWidth={1.5}
            />
          </g>
        ))
      )}
    </svg>
  );
}

const LOGO_BOX_WIDTH = 210;
const HEADER_HEIGHT = 56;

export interface TopHeaderProps {
  onLoggerClick: () => void;
  onChangePasswordClick: () => void;
  projectInfo: ProjectInfo;
}

export function TopHeader({
  onLoggerClick,
  onChangePasswordClick,
}: TopHeaderProps): JSX.Element {
  const {
    currentTheme,
    currentThemeName,
    setTheme,
    canvasBackground,
    setCanvasBackground,
  } = useDexterTheme();
  const dexterAuth = React.useContext(DexterAuthContext);
  const user = dexterAuth.user;
  const client = useApolloClient();
  const history = useHistory();
  const [css] = useStyletron();

  const location = useLocation();
  const isStandaloneReference = location.pathname === Routes.REFERENCE;

  const logoUrl = THEME_LOGOS[currentThemeName] ?? LOGO_URL;
  const themeKeys = React.useMemo(() => Object.keys(themes) as string[], []);

  const { data: appInfoData } = useQuery(gql(appInfo), {
    skip: !isStandaloneReference,
  });
  const appInfoValues = appInfoData?.appInfo as AppInfo | undefined;
  const [isGeneratingDoc, setIsGeneratingDoc] = React.useState(false);

  const handleDownloadDocumentation = async (): Promise<void> => {
    setIsGeneratingDoc(true);
    try {
      const { missingImages } = await generateDocumentationPdf({
        logoUrl: LOGO_URL.toString(),
        appInfo: appInfoValues,
      });
      if (missingImages.length > 0) {
        notify.negative(
          `Documentation downloaded, but ${missingImages.length} screenshot(s) could not be loaded.`
        );
      }
    } catch (error) {
      notify.negative(
        `Could not generate the documentation PDF: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  const handleLogout = React.useCallback(() => {
    sessionStorage.clear();
    client.cache.reset();
    dexterAuth.logoutFromServer();
    history.push("/");
  }, [client, dexterAuth, history]);

  const handleOpenReference = React.useCallback(() => {
    window.open(Routes.REFERENCE, "_blank", "noopener,noreferrer");
  }, []);

  const menuLabelStyle: React.CSSProperties = {
    fontSize: "0.9rem",
    fontFamily: currentTheme.fonts.primary,
    color: currentTheme.colors.headerTextColor,
  };

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        height: `${HEADER_HEIGHT}px`,
        minHeight: `${HEADER_HEIGHT}px`,
        backgroundColor: currentTheme.colors.headerColor,
        color: currentTheme.colors.headerTextColor,
        borderBottom: `1px solid ${currentTheme.colors.avatarBackgroundColor}`,
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: `${LOGO_BOX_WIDTH}px`,
          minWidth: `${LOGO_BOX_WIDTH}px`,
          height: "100%",
          paddingLeft: "16px",
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        <Link
          to={Routes.HOME}
          aria-label="Home"
          style={{
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
            width: "fit-content",
          }}
        >
          <img src={logoUrl.toString()} height={30} alt="Dexter" />
        </Link>
      </div>

      {/* Search bar should eventually go somewhere around here */}
      <div style={{ flex: 1 }} />

      {isStandaloneReference ? (
        <div
          style={{
            paddingRight: "16px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <StatefulTooltip
            placement={PLACEMENT.bottom}
            content="Download the full documentation as a PDF"
            overrides={getTooltipOverrides(currentTheme)}
          >
            <button
              aria-label="Download Documentation"
              onClick={() => void handleDownloadDocumentation()}
              disabled={isGeneratingDoc}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: currentTheme.colors.buttonPrimaryColor,
                color: currentTheme.colors.primaryElementFontColor,
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                fontFamily: currentTheme.fonts.primary,
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: isGeneratingDoc ? "not-allowed" : "pointer",
                opacity: isGeneratingDoc ? 0.6 : 1,
              }}
            >
              <FaDownload size={14} />
              {isGeneratingDoc ? "Generating…" : "Download Documentation"}
            </button>
          </StatefulTooltip>
        </div>
      ) : (
        /* Help + User menu (dropdown) */
        <div
          style={{
            paddingRight: "16px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <StatefulTooltip
            placement={PLACEMENT.bottom}
            content="Open Reference"
            overrides={getTooltipOverrides(currentTheme)}
          >
            <button
              aria-label="Open Reference in a new tab"
              onClick={handleOpenReference}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                background: "transparent",
                border: `0px`,
                color: currentTheme.colors.headerTextColor,
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  fontFamily: currentTheme.fonts.primary,
                  fontWeight: 300,
                  fontSize: "1.35rem",
                  lineHeight: 1,
                }}
              >
                ?
              </span>
            </button>
          </StatefulTooltip>
          <StatefulPopover
            placement={PLACEMENT.bottomRight}
            content={() => (
              <div
                style={{
                  background: currentTheme.colors.headerColor,
                  color: currentTheme.colors.headerTextColor,
                  border: `1px solid ${currentTheme.colors.avatarBackgroundColor}`,
                  borderRadius: "8px",
                  padding: "14px",
                  minWidth: "240px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                }}
              >
                {/* Profile summary */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    paddingBottom: "10px",
                    borderBottom: `1px solid ${currentTheme.colors.avatarBackgroundColor}`,
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: currentTheme.colors.avatarBackgroundColor,
                      border: `1px solid ${currentTheme.colors.avatarBackgroundColor}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontWeight: 600,
                      fontFamily: currentTheme.fonts.primary,
                      fontSize: "1.05rem",
                      color: currentTheme.colors.primaryElementFontColor,
                    }}
                  >
                    {(user?.person?.name?.displayName ?? "U")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: currentTheme.fonts.primary,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        color: currentTheme.colors.headerTextColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {user?.person?.name?.displayName ?? "User"}
                    </div>
                    <div
                      style={{
                        fontFamily: currentTheme.fonts.primary,
                        fontSize: "0.8rem",
                        opacity: 0.75,
                        color: currentTheme.colors.headerTextColor,
                      }}
                    >
                      {user?.isAdmin == null
                        ? "User"
                        : user.isAdmin
                        ? "Admin"
                        : "User"}
                    </div>
                  </div>
                </div>
                {/* Theme selector */}
                <div>
                  <div
                    style={{
                      ...menuLabelStyle,
                      fontSize: "0.8rem",
                      marginBottom: "8px",
                      opacity: 0.85,
                    }}
                  >
                    Dexter Theme
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-around" }}
                  >
                    {themeKeys.map((key) => {
                      const Icon =
                        key === ThemeNames.LIGHT
                          ? FaSun
                          : key === ThemeNames.DARK
                          ? FaMoon
                          : key === ThemeNames.COLORBLIND_DARK
                          ? FaAdjust
                          : FaLowVision;
                      return (
                        <StatefulTooltip
                          key={key}
                          placement={PLACEMENT.bottom}
                          content={THEME_LABELS[key] ?? key}
                          overrides={getTooltipOverrides(currentTheme)}
                        >
                          <div
                            onClick={() => setTheme(key)}
                            className={css({
                              cursor: "pointer",
                              padding: "6px",
                              borderRadius: "4px",
                              backgroundColor:
                                currentThemeName === key
                                  ? currentTheme.colors.headerControlHighlight
                                  : "transparent",
                            })}
                          >
                            <Icon
                              size={20}
                              color={currentTheme.colors.themeIconColor}
                            />
                          </div>
                        </StatefulTooltip>
                      );
                    })}
                  </div>
                </div>

                {/* Canvas background */}
                <div>
                  <div
                    style={{
                      ...menuLabelStyle,
                      fontSize: "0.8rem",
                      marginBottom: "8px",
                      opacity: 0.85,
                    }}
                  >
                    Canvas Background
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-around" }}
                  >
                    {BG_VARIANTS.map((variant) => (
                      <StatefulTooltip
                        key={variant}
                        placement={PLACEMENT.bottom}
                        content={BG_LABELS[variant]}
                        overrides={getTooltipOverrides(currentTheme)}
                      >
                        <div
                          onClick={() => setCanvasBackground(variant)}
                          className={css({
                            cursor: "pointer",
                            padding: "6px",
                            borderRadius: "4px",
                            backgroundColor:
                              canvasBackground === variant
                                ? currentTheme.colors.headerControlHighlight
                                : "transparent",
                          })}
                        >
                          <BgVariantIcon
                            variant={variant}
                            color={currentTheme.colors.themeIconColor}
                          />
                        </div>
                      </StatefulTooltip>
                    ))}
                  </div>
                </div>

                <MenuRow
                  icon={<MdBugReport size={18} />}
                  label="System Logger"
                  onClick={onLoggerClick}
                />
                <MenuRow
                  icon={<MdPassword size={18} />}
                  label="Change Password"
                  onClick={onChangePasswordClick}
                />
                <MenuRow
                  icon={<DeleteAlt size={18} title="" />}
                  label="Logout"
                  onClick={handleLogout}
                />
              </div>
            )}
          >
            <button
              aria-label="User menu"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  background: currentTheme.colors.avatarBackgroundColor,
                  border: `1px solid ${currentTheme.colors.avatarBackgroundColor}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontFamily: currentTheme.fonts.primary,
                  fontSize: "0.95rem",
                  color: currentTheme.colors.primaryElementFontColor,
                }}
              >
                {(user?.person?.name?.displayName ?? "U")
                  .charAt(0)
                  .toUpperCase()}
              </div>
              <FaChevronDown
                size={11}
                color={currentTheme.colors.headerTextColor}
              />
            </button>
          </StatefulPopover>
        </div>
      )}
    </header>
  );
}

function MenuRow({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}): JSX.Element {
  const { currentTheme } = useDexterTheme();
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        background: "transparent",
        border: "none",
        color: currentTheme.colors.headerTextColor,
        cursor: "pointer",
        padding: "6px 4px",
        fontFamily: currentTheme.fonts.primary,
        fontSize: "0.9rem",
        textAlign: "left",
        width: "100%",
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
